import React, { useState, useEffect } from 'react';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import { toast } from 'react-toastify';

interface BreakoutRoomsProps {
    eventId: string;
    isLeader: boolean;
    refreshKey?: number;
}

// ApiClient responses put the payload under res.data (app convention); some endpoints
// return it flat. Unwrap so we read fields the same way regardless.
const unwrap = (res: any) => (res && res.data && typeof res.data === 'object' ? res.data : res);

const BreakoutRooms: React.FC<BreakoutRoomsProps> = ({ eventId, isLeader, refreshKey }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedRound, setSelectedRound] = useState<number | null>(null);
    const [dragged, setDragged] = useState<{ email: string; fromRoom: number } | null>(null);
    const [dragOverRoom, setDragOverRoom] = useState<number | null>(null);
    const [sendingEmails, setSendingEmails] = useState(false);
    // cw: lets you link/change the Zoom meeting ID right on the breakout page.
    const [meetingIdInput, setMeetingIdInput] = useState('');
    // cw: compute the summary numbers on the frontend from the source data directly, so they always
    // populate regardless of what the breakout/rounds endpoint returns: attendeeCount = checked-in
    // count (isPresent, matching the Check-in List), groupSize = the event's configured breakout size.
    const [stats, setStats] = useState<{ attendeeCount: number | null; groupSize: number | null }>({ attendeeCount: null, groupSize: null });

    useEffect(() => {
        if (!eventId) return;
        ApiClient.get('api/attendees/list', { eventId }).then((res: any) => {
            if (res?.success && Array.isArray(res.data)) {
                setStats(s => ({ ...s, attendeeCount: res.data.filter((a: any) => a.isPresent === true).length }));
            }
        }).catch(() => {});
        ApiClient.get('api/event/details', { id: eventId }).then((res: any) => {
            if (res?.success && res.data) {
                const gs = res.data.breakoutGroupSize;
                setStats(s => ({ ...s, groupSize: (gs && gs > 0) ? gs : 4 }));
            }
        }).catch(() => {});
    }, [eventId, refreshKey]);

    const fetchRounds = (selectLatest = false) => {
        setLoading(true);
        ApiClient.get('api/event/breakout/rounds', { eventId }).then((res: any) => {
            setLoading(false);
            if (res?.success) {
                const body = unwrap(res);
                setData(body);
                const rounds = body.rounds || [];
                if (rounds.length) {
                    const latest = Math.max(...rounds.map((r: any) => r.roundNumber));
                    setSelectedRound((prev) => (selectLatest || prev == null ? latest : prev));
                } else {
                    setSelectedRound(null);
                }
            }
        });
    };

    useEffect(() => {
        fetchRounds(true);
    }, [eventId, refreshKey]);

    const handleGenerate = () => {
        loader(true);
        ApiClient.post('api/event/breakout/generate', { eventId }).then((res: any) => {
            loader(false);
            const body = unwrap(res);
            if (res?.success) {
                if (body.warning) toast.warning(body.warning);
                else if (body.round?.roundNumber) toast.success(`Round ${body.round.roundNumber} generated`);
                else toast.success('Breakout round generated');
                // cw: add the new round to state and select it so the view jumps to it immediately.
                // We deliberately do NOT refetch here: an immediate GET after the POST can miss the
                // new round (read-after-write lag), which would clobber this and drop the view back
                // to round 1. Attendee count / group size / max-rounds don't change on generate, so
                // the existing data is still accurate.
                if (body.round?.roundNumber) {
                    setData((prev: any) => prev ? {
                        ...prev,
                        rounds: [...(prev.rounds || []).filter((r: any) => r.roundNumber !== body.round.roundNumber), body.round]
                    } : prev);
                    setSelectedRound(body.round.roundNumber);
                } else {
                    fetchRounds(true);
                }
            } else {
                toast.error(res?.message || res?.error?.message || 'Failed to generate round');
            }
        }).catch((err: any) => {
            loader(false);
            toast.error(err?.response?.data?.message || 'Failed to generate round');
        });
    };

    // Email each attendee a recap of who they were grouped with, per round, for this event.
    const handleSendEmails = () => {
        setSendingEmails(true);
        ApiClient.post('api/event/breakout/send-emails', { eventId }).then((res: any) => {
            setSendingEmails(false);
            const body = unwrap(res);
            if (res?.success) {
                toast.success(`Grouping emails sent to ${body.sent ?? 0} attendee(s)`);
            } else {
                toast.error(res?.message || 'Failed to send grouping emails');
            }
        }).catch((err: any) => {
            setSendingEmails(false);
            toast.error(err?.response?.data?.message || 'Failed to send grouping emails');
        });
    };

    const handleSendToZoom = (roundNumber: number) => {
        loader(true);
        ApiClient.post('api/event/breakout/send-to-zoom', { eventId, roundNumber }).then((res: any) => {
            loader(false);
            if (res?.success) {
                toast.success(unwrap(res).message || 'Exported to Zoom');
                fetchRounds();
            } else {
                toast.error(res?.message || 'Failed to export to Zoom');
            }
        }).catch((err: any) => {
            loader(false);
            toast.error(err?.response?.data?.message || 'Failed to export to Zoom');
        });
    };

    const handleLinkMeeting = () => {
        const mid = meetingIdInput.replace(/\s+/g, '');
        if (!mid) { toast.error('Enter a Zoom meeting ID'); return; }
        loader(true);
        ApiClient.post('api/event/breakout/link-meeting', { eventId, meetingId: mid }).then((res: any) => {
            loader(false);
            if (res?.success) { toast.success('Zoom meeting linked'); setMeetingIdInput(''); fetchRounds(); }
            else toast.error(res?.message || 'Failed to link the Zoom meeting');
        }).catch((err: any) => {
            loader(false);
            toast.error(err?.response?.data?.message || 'Failed to link the Zoom meeting');
        });
    };

    const handleDeleteRound = (roundNumber: number) => {
        if (!window.confirm(`Delete Round ${roundNumber}?`)) return;
        loader(true);
        ApiClient.post('api/event/breakout/delete-round', { eventId, roundNumber }).then((res: any) => {
            loader(false);
            if (res?.success) {
                toast.success(`Round ${roundNumber} deleted`);
                fetchRounds(true);
            } else {
                toast.error(res?.message || 'Failed to delete round');
            }
        }).catch((err: any) => {
            loader(false);
            toast.error(err?.response?.data?.message || 'Failed to delete round');
        });
    };

    const handleMove = (roundNumber: number, email: string, toRoomIndex: number) => {
        // Optimistically move the participant in local state — no full-page loader and no refetch, so
        // the page doesn't flash/refresh. The API call runs in the background; we only resync on error.
        setData((prev: any) => {
            if (!prev) return prev;
            const rounds = (prev.rounds || []).map((r: any) => {
                if (r.roundNumber !== roundNumber) return r;
                const rooms = (r.rooms || []).map((rm: any) => ({ ...rm, participants: [...(rm.participants || [])] }));
                let moved: any = null;
                for (const rm of rooms) {
                    const idx = rm.participants.findIndex((p: any) => p.email && p.email.toLowerCase() === email.toLowerCase());
                    if (idx !== -1) { moved = rm.participants.splice(idx, 1)[0]; break; }
                }
                if (moved && rooms[toRoomIndex]) rooms[toRoomIndex].participants.push(moved);
                return { ...r, rooms };
            });
            return { ...prev, rounds };
        });

        ApiClient.post('api/event/breakout/move-participant', { eventId, roundNumber, email, toRoomIndex }).then((res: any) => {
            if (!res?.success) {
                toast.error(res?.message || 'Failed to move participant');
                fetchRounds();
            }
        }).catch((err: any) => {
            toast.error(err?.response?.data?.message || 'Failed to move participant');
            fetchRounds();
        });
    };

    const handleDropOnRoom = (roundNumber: number, toRoom: number) => {
        setDragOverRoom(null);
        const d = dragged;
        setDragged(null);
        if (d && d.fromRoom !== toRoom) {
            handleMove(roundNumber, d.email, toRoom);
        }
    };

    if (loading) return <div className="p-4 text-gray-500">Loading breakout rooms...</div>;
    if (!data) return null;

    const rounds = [...(data.rounds || [])].sort((a: any, b: any) => a.roundNumber - b.roundNumber);
    const currentRoundCount = rounds.length;
    // Prefer the frontend-computed values (checked-in count + configured group size); fall back to
    // whatever the breakout/rounds endpoint returned. Max unique rounds = (attendees-1)/(groupSize-1),
    // derived from the same numbers shown so it always stays consistent.
    const attendeeCount = stats.attendeeCount ?? data.attendeeCount ?? 0;
    const groupSize = stats.groupSize ?? data.breakoutGroupSize ?? 0;
    const maxPerfectRounds = (attendeeCount > 1 && groupSize > 1)
        ? Math.floor((attendeeCount - 1) / (groupSize - 1))
        : (data.maxPerfectRounds || 0);
    const overMax = maxPerfectRounds > 0 && currentRoundCount > maxPerfectRounds;

    // Which pairs have met more than once, and in which rounds.
    const pairMeetings: { [key: string]: { a: any; b: any; rounds: number[] } } = {};
    rounds.forEach((round: any) => {
        (round.rooms || []).forEach((room: any) => {
            const ps = room.participants || [];
            for (let i = 0; i < ps.length; i++) {
                for (let j = i + 1; j < ps.length; j++) {
                    const pair = [ps[i], ps[j]].sort((x: any, y: any) => (x.email > y.email ? 1 : -1));
                    const key = `${pair[0].email}|${pair[1].email}`;
                    if (!pairMeetings[key]) pairMeetings[key] = { a: pair[0], b: pair[1], rounds: [] };
                    pairMeetings[key].rounds.push(round.roundNumber);
                }
            }
        });
    });
    const repeatPairs = Object.values(pairMeetings)
        .filter(p => p.rounds.length > 1)
        .sort((a, b) => b.rounds.length - a.rounds.length);

    const round = rounds.find((r: any) => r.roundNumber === selectedRound) || rounds[rounds.length - 1];

    // For the currently-viewed round: which participants are grouped with a roommate they've ALSO
    // been grouped with in another round (a repeat pairing). Maps email -> names of the roommate(s)
    // they've met before, so we can flag those exact people in the room view.
    const repeatInfoByEmail: { [email: string]: string[] } = {};
    if (round) {
        (round.rooms || []).forEach((room: any) => {
            const ps = room.participants || [];
            for (let i = 0; i < ps.length; i++) {
                for (let j = i + 1; j < ps.length; j++) {
                    const pair = [ps[i], ps[j]].sort((x: any, y: any) => (x.email > y.email ? 1 : -1));
                    const key = `${pair[0].email}|${pair[1].email}`;
                    if (pairMeetings[key] && pairMeetings[key].rounds.length > 1) {
                        if (!repeatInfoByEmail[ps[i].email]) repeatInfoByEmail[ps[i].email] = [];
                        if (!repeatInfoByEmail[ps[j].email]) repeatInfoByEmail[ps[j].email] = [];
                        repeatInfoByEmail[ps[i].email].push(ps[j].name);
                        repeatInfoByEmail[ps[j].email].push(ps[i].name);
                    }
                }
            }
        });
    }

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg">Breakout Rooms</h4>
                {isLeader && (
                    <div className="flex items-center gap-2">
                        {currentRoundCount > 0 && (
                            <button
                                type="button"
                                onClick={handleSendEmails}
                                disabled={sendingEmails}
                                className="text-white bg-blue-600 hover:bg-blue-700 font-medium rounded-lg text-sm px-4 py-2 disabled:opacity-50"
                            >
                                {sendingEmails ? 'Sending…' : 'Send Grouping Emails'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleGenerate}
                            className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-4 py-2"
                        >
                            Generate Next Round ({currentRoundCount + 1})
                        </button>
                    </div>
                )}
            </div>

            {/* Prominent summary + colored max-unique-rounds indicator */}
            <div className={`rounded-lg p-4 mb-4 border ${overMax ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex flex-wrap justify-between items-center gap-y-1 text-sm">
                    <span><span className="font-semibold">{attendeeCount}</span> Attendees</span>
                    <span>Groups of <span className="font-semibold">{groupSize}</span></span>
                    <span>Rounds generated: <span className="font-semibold">{currentRoundCount}</span></span>
                    <span className={`px-3 py-1 rounded-full font-semibold ${overMax ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        Max unique rounds: {maxPerfectRounds}
                    </span>
                </div>
                {overMax && (
                    <p className="text-xs text-amber-700 mt-2">
                        You've generated more than the {maxPerfectRounds} unique round{maxPerfectRounds === 1 ? '' : 's'} possible
                        ({attendeeCount} attendees ÷ groups of {groupSize}). Some people will meet again — see below.
                    </p>
                )}
            </div>

            {/* Repeat-pairing warning — these people have been grouped together in more than one round */}
            {repeatPairs.length > 0 && (
                <div className="rounded-lg p-4 mb-4 bg-red-50 border border-red-300">
                    <p className="font-semibold text-sm mb-1 text-red-700 flex items-center gap-1">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500"></span>
                        {repeatPairs.length} repeat pairing{repeatPairs.length === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-red-600 mb-2">These people have been placed together in more than one round — they've already met.</p>
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                        {repeatPairs.map((p, i) => (
                            <li key={i} className="text-xs text-red-700">
                                <span className="font-medium">{p.a.name}</span> &amp; <span className="font-medium">{p.b.name}</span>
                                <span className="text-red-400"> — together in rounds {p.rounds.join(', ')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {rounds.length === 0 && (
                <p className="text-gray-400 text-sm">No rounds generated yet.</p>
            )}

            {/* Round selector */}
            {rounds.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                    <label className="text-sm text-gray-600">Viewing:</label>
                    <select
                        className="shadow-box bg-white rounded-lg h-9 px-2 text-sm"
                        value={round?.roundNumber ?? ''}
                        onChange={(e) => setSelectedRound(Number(e.target.value))}
                    >
                        {rounds.map((r: any) => (
                            <option key={r.roundNumber} value={r.roundNumber}>
                                Round {r.roundNumber}{r.sentToZoom ? ' (exported)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {round && (
                <div className="mb-6 bg-white rounded-lg shadow-box p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <h5 className="font-medium">Round {round.roundNumber}</h5>
                            {round.sentToZoom && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Exported to Zoom</span>
                            )}
                        </div>
                        {isLeader && (
                            <button
                                type="button"
                                onClick={() => handleDeleteRound(round.roundNumber)}
                                className="text-red-500 hover:text-red-700 text-sm"
                            >
                                Delete
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {round.rooms.map((room: any, ri: number) => (
                            <div
                                key={ri}
                                className={`border rounded-lg p-3 transition-colors ${dragOverRoom === ri ? 'border-orange-500 bg-orange-50' : ''}`}
                                onDragOver={isLeader ? (e) => { e.preventDefault(); setDragOverRoom(ri); } : undefined}
                                onDragLeave={isLeader ? () => setDragOverRoom(null) : undefined}
                                onDrop={isLeader ? () => handleDropOnRoom(round.roundNumber, ri) : undefined}
                            >
                                <p className="font-medium text-sm mb-2">{room.name}</p>
                                <ul className="space-y-1.5">
                                    {room.participants.map((p: any, pi: number) => (
                                        <li
                                            key={pi}
                                            draggable={isLeader && round.rooms.length > 1}
                                            onDragStart={isLeader ? () => setDragged({ email: p.email, fromRoom: ri }) : undefined}
                                            onDragEnd={() => { setDragged(null); setDragOverRoom(null); }}
                                            className={`text-xs text-gray-600 flex items-center justify-between gap-2 ${isLeader && round.rooms.length > 1 ? 'cursor-move' : ''}`}
                                        >
                                            <span className="flex items-center gap-1 min-w-0">
                                                <span className={`w-1.5 h-1.5 rounded-full inline-block flex-shrink-0 ${repeatInfoByEmail[p.email] ? 'bg-red-500' : 'bg-orange-400'}`}></span>
                                                <span className="truncate">{p.name} <span className="text-gray-400">({p.email})</span></span>
                                                {repeatInfoByEmail[p.email] && (
                                                    <span
                                                        className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full flex-shrink-0"
                                                        title={`Met before with ${Array.from(new Set(repeatInfoByEmail[p.email])).join(', ')}`}
                                                    >
                                                        met before
                                                    </span>
                                                )}
                                            </span>
                                            {isLeader && round.rooms.length > 1 && (
                                                <select
                                                    className="text-xs bg-orange-400 text-white rounded px-2 py-0.5 cursor-pointer focus:outline-none flex-shrink-0"
                                                    value=""
                                                    onChange={(e) => {
                                                        if (e.target.value !== '') handleMove(round.roundNumber, p.email, Number(e.target.value));
                                                    }}
                                                    title="Move to another room"
                                                >
                                                    <option value="">Move to</option>
                                                    {round.rooms.map((r2: any, idx: number) => (
                                                        idx !== ri ? <option key={idx} value={idx}>{r2.name}</option> : null
                                                    ))}
                                                </select>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {isLeader && data.zoomMeetingId && (
                        <button
                            type="button"
                            onClick={() => handleSendToZoom(round.roundNumber)}
                            className="mt-4 w-full text-white bg-blue-600 hover:bg-blue-700 font-semibold rounded-lg text-sm py-3"
                        >
                            {round.sentToZoom ? 'Re-export' : 'Export'} to Zoom breakout rooms
                        </button>
                    )}
                    {isLeader && !data.zoomMeetingId && (
                        <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Link a Zoom meeting to export breakout rooms. Paste the Meeting ID of a meeting in your connected Zoom account.</p>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="text"
                                    value={meetingIdInput}
                                    onChange={e => setMeetingIdInput(e.target.value)}
                                    placeholder="Zoom Meeting ID (e.g. 123 456 7890)"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <button
                                    type="button"
                                    onClick={handleLinkMeeting}
                                    className="text-white bg-blue-600 hover:bg-blue-700 font-semibold rounded-lg text-sm px-5 py-2"
                                >
                                    Link meeting
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BreakoutRooms;
